import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Alert } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Delete, CameraAlt } from '@mui/icons-material';
import theme from '../../themes';

import { useActiveStepContext } from '../../hooks/use-active-step-context';
import useTranslation from '../customHooks/translations';
import UploadPhotoTitlePopover from './UploadPhotoTitlePopover';
import { prepareImageForUpload } from '../../utils/prepare-image-for-upload';

const Dropbox = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 18rem;
  border-radius: 1rem;
  padding: 1.25rem 1.5rem 1.5rem;
  width: 100%;
  opacity: ${(props) => (props.itemdraggedover ? '0.5' : '1')};
  background: transparent;
  border: 2px dashed ${(props) => props.color || '#0D9488'};
  svg {
    color: ${(props) => props.color || '#0D9488'};
  }
  #browse {
    padding: 0.6rem 1.4rem;
    cursor: pointer;
    border-radius: 999px;
    background: #0D9488;
    color: white;
    font-size: 1.125rem;
    font-weight: 600;
    :hover {
      opacity: 0.85;
      background: #0F766E;
    }
    margin-top: 1em;
  }
  #image-upload {
    display: none;
  }
  #image-preview {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    flex-direction: column;
  }
  #delete {
    border-radius: 50px;
    border: none;
    width: 100px;
    opacity: 0.85;
    cursor: pointer;
    z-index: 100;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: 0.5em;
    color: white;
  }
`;

const ImageValidationPanel = ({
  selectedImage,
  setSelectedImage,
  actionStyle = {},
  disabled = false,
}) => {
  const [itemDrag, setItemDrag] = useState(false);
  const [selectedImagePreview, setSelectedImagePreview] = useState();
  const [fileTypeError, setFileTypeError] = useState(false);
  const previewUrlRef = useRef(null);
  const activeStepContext = useActiveStepContext();

  // Restore preview when selectedImage exists but preview was lost (e.g. browser Back)
  useEffect(() => {
    if (selectedImage && !selectedImagePreview) {
      const url = URL.createObjectURL(selectedImage);
      previewUrlRef.current = url;
      setSelectedImagePreview(url);
    }
    if (!selectedImage && selectedImagePreview) {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
      setSelectedImagePreview(undefined);
    }
  }, [selectedImage, selectedImagePreview]);

  useEffect(() => {
    return () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
      }
    };
  }, []);

  const dragOverHandler = (e) => {
    e.preventDefault();
    if (!disabled) setItemDrag(true);
  };

  const dragLeaveHandler = () => {
    setItemDrag(false);
  };

  //normalizes the file for upload (HEIC/oversized images are converted to jpeg)
  const processSelectedFile = async (file) => {
    if (!file || disabled) {
      return;
    }
    try {
      const preparedImage = await prepareImageForUpload(file);
      setFileTypeError(false);
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
      }
      setSelectedImage(preparedImage);
      const url = URL.createObjectURL(preparedImage);
      previewUrlRef.current = url;
      setSelectedImagePreview(url);
    } catch (error) {
      if (error?.code === 'UNSUPPORTED_IMAGE') {
        setFileTypeError(true);
      } else {
        throw error;
      }
    }
  };

  const dropHandler = async (e) => {
    if (disabled) return;
    // if the file is uploaded from the input button
    if (e.target.files) {
      await processSelectedFile(e.target.files[0]);
      // if the file is uploaded from the drag and drop box
    } else if (e.dataTransfer.files) {
      e.preventDefault();
      setItemDrag(false);
      if (!e.dataTransfer.files || e.dataTransfer.files.length === 0) {
        // Non-file drops should not clear an already-selected image
        return;
      }
      await processSelectedFile(e.dataTransfer.files[0]);
    }
  };

  const handleDelete = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setSelectedImage(null);
    setSelectedImagePreview(undefined);
  };

  const translation = useTranslation();

  return (
    <>
      <UploadPhotoTitlePopover />
      <Box
        sx={{
          width: { md: '100%' },
          maxWidth: { xs: '100%', md: '28rem'},
          opacity: disabled ? 0.6 : 1,
          pointerEvents: disabled ? 'none' : 'auto',
        }}
      >
        {fileTypeError && (
          <Alert severity="error" onClose={() => setFileTypeError(false)}>
            {translation.imageValidationError}
          </Alert>
        )}
        <Dropbox
          onDragOver={dragOverHandler}
          onDragLeave={dragLeaveHandler}
          onDrop={dropHandler}
          itemdraggedover={itemDrag ? 1 : 0}
          color={
            activeStepContext?.actionStyle
              ? activeStepContext?.actionStyle?.color
              : actionStyle?.color
          }
        >
          {selectedImage ? (
            <>
              <Typography
                component="div"
                variant="subtitle2"
                sx={{ my: '0.5em', fontWeight: '800' }}
                color={theme.palette.common.dark}
              >
                {translation.imageValidationSelected}
              </Typography>
              <Box id="image-preview" sx={{ width: '100%' }}>
                {selectedImagePreview && (
                  <Box
                    component="img"
                    sx={{
                      maxHeight: { xs: 110, md: 350 },
                      maxWidth: { xs: 110, md: 350 },
                      mt: '1em',
                    }}
                    alt={translation.imageValidationSelected || 'Uploaded action photo'}
                    src={selectedImagePreview}
                  />
                )}
                <button
                  type="button"
                  id="delete"
                  style={{ backgroundColor: '#DC2626', margin: '0.5rem 0' }}
                  onClick={handleDelete}
                  disabled={disabled}
                >
                  <Delete />
                  {translation.delete}
                </button>
              </Box>
            </>
          ) : (
            <>
              <CameraAlt sx={{ fontSize: { xs: '4.5rem', md: '5.5rem' } }} />
              <label htmlFor="image-upload" id="browse">
                <Typography
                  component="span"
                  variant="body1"
                  sx={{ fontWeight: 600, color: '#FFFFFF' }}
                >
                  {translation.selectFile}
                </Typography>
              </label>
              <input
                accept="image/*"
                id="image-upload"
                type="file"
                onChange={dropHandler}
                disabled={disabled}
              />
            </>
          )}
        </Dropbox>
      </Box>
    </>
  );
};

export default ImageValidationPanel;
